import { useState } from 'react';
import {
  IonContent,
  IonPage,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonInput,
  IonTextarea,
  IonToggle,
  IonItem,
  IonLabel,
  IonButton,
  IonSpinner,
  useIonRouter,
  useIonToast,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import { GET_MY_CHANNEL } from '../../../../graphql/queries/myChannel';
import { UPDATE_CHANNEL } from '../../../../graphql/mutations/updateChannel';
import type {
  GetMyChannelQuery,
  UpdateChannelMutation,
  UpdateChannelMutationVariables,
} from '../../../../types/graphql-generated';
import { AppSectionHeader } from '../../../components/ui/AppSectionHeader';

const NAME_MAX_LENGTH = 36;
const DESCRIPTION_MAX_LENGTH = 512;

export const EditChannel: React.FC = () => {
  const ionRouter = useIonRouter();
  const [presentToast] = useIonToast();
  const channelId = useParams<'channelId'>().channelId!;

  const { data, loading: loadingChannel } = useQuery<GetMyChannelQuery>(GET_MY_CHANNEL, {
    variables: { channelId },
  });
  const [updateChannel, { loading: saving }] = useMutation<
    UpdateChannelMutation,
    UpdateChannelMutationVariables
  >(UPDATE_CHANNEL);

  const channel = data?.myChannel;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [allowComments, setAllowComments] = useState(true);
  const [publishAuthorName, setPublishAuthorName] = useState(true);

  // Seed the form from the loaded channel exactly once. Adjusting state during
  // render (guarded by the channel id) is the React-endorsed alternative to a
  // setState-in-effect and keeps the inputs editable afterwards.
  const [seededId, setSeededId] = useState<string | null>(null);
  if (channel && channel.id !== seededId) {
    setSeededId(channel.id);
    setName(channel.name ?? '');
    setDescription(channel.description ?? '');
    setAllowComments(channel.allowComments ?? true);
    setPublishAuthorName(channel.publishAuthorName ?? true);
  }

  const showToast = (message: string, danger = false) => {
    presentToast({
      message,
      duration: 3000,
      position: 'bottom',
      positionAnchor: 'app-footer',
      cssClass: danger ? 'toast-danger' : 'toast-success',
      buttons: [{ icon: closeOutline, role: 'cancel' }],
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!channel) {
      return;
    }

    try {
      const result = await updateChannel({
        variables: {
          input: {
            channelId: channel.id,
            name,
            description,
            allowComments,
            publishAuthorName,
          },
        },
      });

      const errors = result.data?.updateChannel?.errors ?? [];
      if (errors.length > 0) {
        showToast(errors[0], true);
        return;
      }

      showToast('Canal actualizado');
      ionRouter.goBack();
    } catch {
      showToast('No se pudo actualizar el canal. Inténtalo de nuevo.', true);
    }
  };

  return (
    <IonPage>
      <AppSectionHeader variant="main">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/main/perfil" />
          </IonButtons>
          <IonTitle className="ml-4">Editar canal</IonTitle>
        </IonToolbar>
      </AppSectionHeader>

      <IonContent className="ion-padding">
        {loadingChannel ? (
          <div className="flex justify-center items-center min-h-[200px]">
            <IonSpinner name="crescent" />
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <div className="flex flex-col gap-1">
              <label htmlFor="channel-name" className="text-sm font-semibold text-gray-700">
                Nombre
              </label>
              <IonInput
                id="channel-name"
                className="w-full text-start"
                fill="outline"
                value={name}
                maxlength={NAME_MAX_LENGTH}
                onIonInput={(e) => setName(e.detail.value ?? '')}
                placeholder="Nombre del canal"
                aria-label="Nombre del canal"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="channel-description" className="text-sm font-semibold text-gray-700">
                Descripción del canal
              </label>
              <IonTextarea
                id="channel-description"
                className="w-full text-start"
                fill="outline"
                value={description}
                maxlength={DESCRIPTION_MAX_LENGTH}
                autoGrow
                counter
                onIonInput={(e) => setDescription(e.detail.value ?? '')}
                placeholder="Describe tu canal"
                aria-label="Descripción del canal"
              />
            </div>

            <IonItem lines="none" className="px-0">
              <IonLabel>Permitir comentarios</IonLabel>
              <IonToggle
                checked={allowComments}
                onIonChange={(e) => setAllowComments(e.detail.checked)}
                slot="end"
                aria-label="Permitir comentarios"
              />
            </IonItem>

            <IonItem lines="none" className="px-0">
              <IonLabel>Mostrar el autor de las publicaciones</IonLabel>
              <IonToggle
                checked={publishAuthorName}
                onIonChange={(e) => setPublishAuthorName(e.detail.checked)}
                slot="end"
                aria-label="Mostrar el autor de las publicaciones"
              />
            </IonItem>

            <IonButton expand="block" type="submit" disabled={saving}>
              {saving ? <IonSpinner name="crescent" /> : 'Guardar'}
            </IonButton>
          </form>
        )}
      </IonContent>
    </IonPage>
  );
};
